import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Button, Divider, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Modal, OutlinedInput, Select } from '@mui/material';
import Typography from '@mui/material/Typography';

import { Formik } from 'formik';
import * as Yup from 'yup';

import MainCard from '../../../components/ui-component/cards/MainCard';
import AnimateButton from '../../../components/ui-component/extended/AnimateButton';
import { useTheme } from '@mui/material/styles';
import { DefaultTextModal } from '../../../components/ui-component/Modal';
import { actDevice } from '../../../../api/post/Device';
import { RESPONSE_CODE, responseResult } from '../../../../metadata/constant';
import { RedAsteriskLabel } from '../../../components/ui-component/Label';

// ==============================|| CENTER PATIENT SERVICE ||============================== //

const DeviceRegister = () => {
    const navigate = useNavigate();

    const theme = useTheme();
    const [openModal, setOpenModal] = useState(0);
    const [deviceActCode, setDeviceActCode] = useState(0);

    const handleModalClose = () => {
        setOpenModal(0);
    };

    const handleModalSubmit = () => {
        setOpenModal(0);
        navigate('/management/license');
    };

    return (
        <>
            <Grid container rowSpacing={4.5} columnSpacing={2.75}>
                <Grid item xs={12}>
                    <MainCard title="기기 등록">
                        <Formik
                            initialValues={{
                                name: '',
                                memo: '',
                                submit: null
                            }}
                            validationSchema={Yup.object().shape({
                                name: Yup.string().min(5).max(50).required('기기명을 입력해 주세요'),
                                memo: Yup.string().min(0).max(500).optional()
                            })}
                            onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                                try {
                                    const response = await actDevice({
                                        device_name: values.name,
                                        memo: values.memo
                                    });

                                    if (responseResult(response.code) === RESPONSE_CODE.SUCCESS) {
                                        setDeviceActCode(response.data.code);
                                        setStatus({ success: true });
                                        setSubmitting(false);
                                        setOpenModal(1);
                                    }
                                } catch (err) {
                                    setStatus({ success: false });
                                    setErrors({ submit: err.message || '등록 중 오류가 발생했습니다.' });
                                    setSubmitting(false);
                                    setOpenModal(2);
                                }
                            }}
                        >
                            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, setFieldValue }) => (
                                <form noValidate onSubmit={handleSubmit} autoComplete="off">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth error={Boolean(touched.name && errors.name)}>
                                                <RedAsteriskLabel required htmlFor="outlined-adornment-name">
                                                    기기명
                                                </RedAsteriskLabel>
                                                <OutlinedInput
                                                    id="outlined-adornment-name"
                                                    type="text"
                                                    value={values.name}
                                                    name="name"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    label="기기명"
                                                />
                                                {touched.name && errors.name && (
                                                    <FormHelperText error id="standard-weight-helper-text-name">
                                                        {errors.name}
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth error={Boolean(touched.memo && errors.memo)}>
                                                <InputLabel htmlFor="outlined-adornment-memo">메모</InputLabel>
                                                <OutlinedInput
                                                    id="outlined-adornment-memo"
                                                    type="text"
                                                    value={values.memo}
                                                    name="memo"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    label="메모"
                                                />
                                                {touched.memo && errors.memo && (
                                                    <FormHelperText error id="standard-weight-helper-text-memo">
                                                        {errors.memo}
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={theme.typography.buttonBox}>
                                                <AnimateButton>
                                                    <Button
                                                        disableElevation
                                                        disabled={isSubmitting}
                                                        size="large"
                                                        color="secondary"
                                                        onClick={() => {
                                                            navigate(-1);
                                                        }}
                                                    >
                                                        취소하고 돌아가기
                                                    </Button>
                                                </AnimateButton>
                                                <AnimateButton>
                                                    <Button
                                                        disableElevation
                                                        disabled={isSubmitting}
                                                        size="large"
                                                        type="submit"
                                                        variant="contained"
                                                        color="primary"
                                                    >
                                                        신규 등록
                                                    </Button>
                                                </AnimateButton>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </form>
                            )}
                        </Formik>
                    </MainCard>
                </Grid>
            </Grid>
            <Modal open={openModal === 1} onClose={handleModalClose}>
                <Box sx={theme.typography.modal}>
                    <Typography id="modal-modal-title" variant="h3" component="h2">
                        신규 기기 등록 완료
                    </Typography>
                    <Typography id="modal-modal-description" sx={{ mt: 2, lineHeight: 1.8 }}>
                        신규 기기가 등록되었습니다.
                        <br />
                        아래의 인증 번호를 브레인 가드 닥터 서비스의 인증 번호란에 입력하여 등록을 진행해 주세요.
                        <br />
                        자세한 기기 등록 방법은 사용자 메뉴얼을 참고해주세요.
                    </Typography>
                    <Typography id="modal-modal-content" variant="h1" component="h1" sx={{ margin: 5, justifySelf: 'center' }}>
                        {deviceActCode}
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <AnimateButton>
                            <Button
                                disableElevation
                                size="large"
                                type="submit"
                                variant="contained"
                                color="primary"
                                onClick={handleModalSubmit}
                            >
                                확인
                            </Button>
                        </AnimateButton>
                    </Box>
                </Box>
            </Modal>
            <DefaultTextModal
                title={`기기 등록 실패`}
                content={`기기 정보 등록에 실패하였습니다.\n잠시 후 다시 시도해 주세요.\n오류가 지속되는 경우 고객센터로 문의 부탁드립니다.`}
                open={openModal === 2}
                onClose={handleModalClose}
            />
        </>
    );
};

export default DeviceRegister;
